import useAppContext from '@groupefungo/react-apollo.core/app/UseContext';
import usePageInfoFragment from '../../../../../base/hooks/PageInfoFragment';

export default () => {
  const { gql, useQuery, useMutation } = useAppContext();
  const { PageInfoFragment } = usePageInfoFragment();

  const UserFragment = gql`
  fragment UserFragment on User {
    id
    gid
    email
    firstName
    lastName
    fullName
    roleIds
    roles {
      id
      name
    }
  }
  `;

  const UserConnectionGql = gql`
  query users($first: Int, $last: Int, $after: String, $before: String, $filters: FiltersInput) {
  users(first: $first, last: $last, after: $after, before: $before, filters: $filters) {
    edges {
      node {
        ...UserFragment
      }
    }
    pageInfo {
      ...PageInfoFragment
    }
  }
}
${UserFragment}
${PageInfoFragment}
  `;

  const UserNodeGql = gql`
  query userNode($gid: ID!) {
    node(gid: $gid) {
      ...UserFragment
    }
  }
  ${UserFragment}
  `;

  const UpdateUserMutation = gql`
    mutation updateUser($input: UserInput!) {
      updateUser(input: $input) {
        user {
          ...UserFragment
        }
      }
    }
${UserFragment}
`;

  const CreateUserMutation = gql`
    mutation createUser($input: UserInput!) {
      createUser(input: $input) {
        user {
          ...UserFragment
        }
      }
    }
${UserFragment}
`;

  const NewUserGql = gql`
query {
  newUser {
      email
      password
      firstName
      lastName
      roleIds
  }
}  
  `;

  const MeGql = gql`
  query me {
    me {
      ...UserFragment
    }
  }
  ${UserFragment}
`;

  const StylistsGql = gql`
query stylists($availabilityId: ID) {
  stylists {
    ...UserFragment
    booked(availabilityId: $availabilityId)
    reserved(availabilityId: $availabilityId)
    conflicts(availabilityId: $availabilityId){
      availability{
        id
      }
      startTime
      endTime
    }
  }
}  
${UserFragment}
  `;

  const FittersGql = gql`
query fitters($availabilityId: ID) {
  fitters {
    ...UserFragment
    booked(availabilityId: $availabilityId)
    reserved(availabilityId: $availabilityId)
    conflicts(availabilityId: $availabilityId){
      availability{
        id
      }
      startTime
      endTime
    }
  }
}  
${UserFragment}
  `;

  const useUpdateUserMutation = (input) => useMutation(UpdateUserMutation, {
    variables: { input },
  });

  const useCreateUserMutation = (input) => useMutation(CreateUserMutation, {
    variables: { input },
  });

  const useMeQuery = () => useQuery(MeGql, {
    fetchPolicy: 'network-only',
  });

  const useStylistsQuery = (availabilityId = null) => useQuery(StylistsGql, {
    fetchPolicy: 'network-only',
    variables: { availabilityId },
  });

  const useFittersQuery = (availabilityId = null) => useQuery(FittersGql, {
    fetchPolicy: 'network-only',
    variables: { availabilityId },
  });

  const useUserConnectionQuery = (connection) => {
    const { first, last, before, after, ...filters } = connection;
    const variables = { first, last, before, after, filters };

    return useQuery(UserConnectionGql, {
      variables,
      fetchPolicy: 'network-only',
    });
  };

  const useUserNodeQuery = (gid) => useQuery(UserNodeGql, {
    variables: { gid },
    fetchPolicy: 'network-only',
  });

  const useNewUserQuery = () => useQuery(NewUserGql, {
    fetchPolicy: 'network-only',
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const useUserQuery = (isNewUser, gid) => (isNewUser ? useNewUserQuery() : useUserNodeQuery(gid));
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const useUserMutation = (user) => (user.id ? useUpdateUserMutation(user) : useCreateUserMutation(user));

  return {
    useMeQuery,
    useUserConnectionQuery,
    useUserQuery,
    useUserMutation,
    useStylistsQuery,
    useFittersQuery,
    UserFragment,
  };
};
